import { Users, Briefcase } from 'lucide-react'
import type { FleetRecord } from '../../../types'
import { serviceAssets } from '../../../constants/assets'

type FleetCardProps = {
  fleet: FleetRecord
  onEdit: (id: string) => void
  onToggleActive: (id: string) => void
  isUpdating: boolean
}

export const FleetCard = ({ fleet, onEdit, onToggleActive, isUpdating }: FleetCardProps) => {
  return (
    <div className="flex flex-col justify-between rounded-[16px] border border-[#C5E1A5] bg-white p-[16px] shadow-[0px_2px_8px_rgba(0,0,0,0.04)]">
      <div>       

        {/* Header Row: Display Order, Category & Actions */}
        <div className="relative mb-3 flex items-center justify-between">
          {/* Display Order */}
          <div className="flex items-center gap-2">
            <div
              className="flex size-[28px] items-center justify-center rounded-[8px] bg-[#1A1A1A] font-host text-[12px] font-bold text-white shadow-sm"
              title="Display order"
            >
              {fleet.displayOrder ?? 0}
            </div>

            {/* <span className="font-host text-[9px] font-semibold uppercase tracking-wide text-[#999999]">
              Order
            </span> */}
          </div>

          {/* Category - Center */}
          <span className="absolute left-1/2 -translate-x-1/2 rounded-full bg-[#C5E1A5] px-[10px] py-[4px] font-host text-[10px] font-bold uppercase tracking-wide text-[#33691E]">
            {fleet.category}
          </span>

          {/* Edit */}
          <div className="ml-auto flex items-center gap-[6px]">
            <button
              onClick={() => onEdit(fleet.id)}
              className="flex size-[22px] items-center justify-center rounded-[6px] border border-[#E0E0E0] text-[#757575] transition-colors hover:bg-gray-50"
            >
              <img
                src={serviceAssets.edit}
                alt="Edit"
                className="size-[12px]"
              />
            </button>
          </div>
        </div>

        {/* Title Row: Name & Plate */}
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="font-host text-[18px] font-bold text-[#1A1A1A]">
            {fleet.name}
          </h3>

          <span className="font-host text-[14px] font-bold text-[#1A1A1A]">
            {fleet.plate}
          </span>
        </div>

        {/* Vehicle Image */}
        <div className="mb-4 flex h-[110px] items-center justify-center">
          <img
            src={fleet.image || serviceAssets.vehicleCar}
            alt={fleet.name}
            className="max-h-full max-w-full object-contain drop-shadow-md"
          />
        </div>

        {/* Specs Row */}
        <div className="mb-3 flex items-center justify-between px-2">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1.5 text-[#555555]">
              <Users size={15} strokeWidth={2.5} />
              <span className="font-host text-[13px] font-bold leading-none">
                {fleet.passengers}
              </span>
            </div>

            <span className="mt-1 font-host text-[10px] text-[#888888]">
              Passengers
            </span>
          </div>

          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1.5 text-[#555555]">
              <Briefcase size={15} strokeWidth={2.5} />
              <span className="font-host text-[13px] font-bold leading-none">
                {fleet.luggage}
              </span>
            </div>

            <span className="mt-1 font-host text-[10px] text-[#888888]">
              Luggage
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mb-3 h-px w-full bg-[#EEEEEE]" />

        {/* Description */}
        <p className="min-h-[32px] line-clamp-2 font-host text-[11px] leading-[1.4] text-[#666666]">
          {fleet.description}
        </p>
      </div>

      {/* Footer: Status & Toggle */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-host text-[12px] font-semibold text-[#1A1A1A]">
            Status
          </span>

          <span className="rounded-full bg-[#E8F5E9] px-[10px] py-[3px] font-host text-[11px] font-medium text-[#2E7D32]">
            {fleet.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Custom Toggle Switch */}
        <button
          type="button"
          role="switch"
          aria-checked={fleet.active}
          disabled={isUpdating}
          onClick={() => onToggleActive(fleet.id)}
          className={`relative inline-flex h-[22px] w-[40px] shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A1A] focus-visible:ring-offset-2 ${
            fleet.active ? 'bg-[#1A1A1A]' : 'bg-[#E0E0E0]'
          }`}
        >
          <span className="sr-only">
            Toggle active status
          </span>

          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block size-[18px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              fleet.active ? 'translate-x-[18px]' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  )
}